import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import api from '../../api';
import usePlayerMeta, { normName } from './usePlayerMeta';
import { fmtCap, NHL_CAP_MIN, pkey, draftPts, LAST_SEASON } from './poolFx';

const nhlLogo = (team) => `https://assets.nhle.com/logos/nhl/svg/${team}_light.svg`;

function abbrevInjury(status) {
  const s = (status || '').toLowerCase();
  if (s.includes('day-to-day')) return 'DTD';
  if (s.includes('ir')) return 'IR';
  return 'OUT';
}

export default function TemplateEditor({ templateId, userId, onBack, lang = 'en' }) {
  const t = lang === 'fr' ? TR.fr : TR.en;
  const [template, setTemplate] = useState(null);
  const [entries, setEntries] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [posFilter, setPosFilter] = useState('ALL');
  const [teamFilter, setTeamFilter] = useState('ALL');
  const [playoffOnly, setPlayoffOnly] = useState(false);
  const [hideInjured, setHideInjured] = useState(false);
  const { playoffTeams, injuryMap } = usePlayerMeta();
  const [dragSourceIdx, setDragSourceIdx] = useState(null);  // reorder source
  const [dragOverIdx, setDragOverIdx] = useState(null);      // current hover target
  const [dragOverEnd, setDragOverEnd] = useState(false);     // hovering the empty end zone
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [mobileTab, setMobileTab] = useState('list'); // phone layout: list | add

  const fetchData = useCallback(async () => {
    const [{ data: tpl }, { data: ent }] = await Promise.all([
      supabase.from('user_queue_templates').select('*').eq('id', templateId).single(),
      supabase.from('user_queue_entries').select('*').eq('template_id', templateId).order('rank', { ascending: true }),
    ]);
    if (tpl) setTemplate(tpl);
    if (ent) setEntries(ent);
  }, [templateId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    // keeps refreshing while the server is still building the NHL list (after a restart)
    return api.watchRosters((players, updating) => {
      if (players.length) setAllPlayers(players);
      if (players.length || !updating) setPlayersLoading(false);
    }, () => { setError(t.errRosters); setPlayersLoading(false); });
  }, [t.errRosters]);

  const entryIds = useMemo(() => new Set(entries.map((e) => e.player_id)), [entries]);
  const capById = useMemo(() => new Map(allPlayers.map((p) => [pkey(p.id), p.capHit ?? NHL_CAP_MIN])), [allPlayers]);

  const teams = useMemo(() => {
    const set = new Set(allPlayers.map((p) => p.team).filter(Boolean));
    return ['ALL', ...Array.from(set).sort()];
  }, [allPlayers]);

  const filteredPlayers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allPlayers
      .filter((p) => !entryIds.has(p.id))
      .filter((p) => posFilter === 'ALL' || p.position === posFilter)
      .filter((p) => teamFilter === 'ALL' || p.team === teamFilter)
      .filter((p) => !q || p.name.toLowerCase().includes(q))
      .filter((p) => !playoffOnly || !playoffTeams || playoffTeams.size === 0 || playoffTeams.has(p.team))
      .filter((p) => !hideInjured || !injuryMap || !injuryMap.has(normName(p.name)))
      .sort((a, b) => draftPts(b) - draftPts(a));   // last season's points
  }, [allPlayers, entryIds, posFilter, teamFilter, search, playoffOnly, playoffTeams, hideInjured, injuryMap]);

  const handleAdd = async (player) => {
    setError(''); setBusy(true);
    const { data, error: rpcErr } = await supabase.rpc('add_to_queue', {
      p_user_id: userId,
      p_template_id: templateId,
      p_player_id: player.id,
      p_player_name: player.name,
      p_team: player.team,
      p_position: player.position,
    });
    setBusy(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errAdd); return; }
    fetchData();
  };

  const handleRemove = async (entryId) => {
    setError(''); setBusy(true);
    const { data, error: rpcErr } = await supabase.rpc('remove_from_queue', {
      p_user_id: userId, p_entry_id: entryId,
    });
    setBusy(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errRemove); return; }
    fetchData();
  };

  const moveEntryToIndex = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx === toIdx - 1) return;
    const reordered = [...entries];
    const [moved] = reordered.splice(fromIdx, 1);
    const insertAt = toIdx > fromIdx ? toIdx - 1 : toIdx;
    reordered.splice(insertAt, 0, moved);
    setEntries(reordered); // optimistic
    const { error: rpcErr } = await supabase.rpc('reorder_queue', {
      p_user_id: userId,
      p_template_id: templateId,
      p_ordered_ids: reordered.map((e) => e.id),
    });
    if (rpcErr) { setError(rpcErr.message); fetchData(); }
  };

  const addPlayerAtIndex = async (player, toIdx) => {
    setError(''); setBusy(true);
    const { data, error: rpcErr } = await supabase.rpc('add_to_queue', {
      p_user_id: userId,
      p_template_id: templateId,
      p_player_id: player.id,
      p_player_name: player.name,
      p_team: player.team,
      p_position: player.position,
    });
    if (rpcErr) { setBusy(false); setError(rpcErr.message); return; }
    if (!data?.success) { setBusy(false); setError(data?.error || t.errAdd); return; }

    // Fetch fresh entries so we know the new entry id, then move to the drop index.
    const { data: fresh } = await supabase.from('user_queue_entries')
      .select('*').eq('template_id', templateId).order('rank', { ascending: true });
    setBusy(false);
    if (!fresh) { fetchData(); return; }
    if (toIdx === null || toIdx >= fresh.length) {
      setEntries(fresh);
      return;
    }
    const newEntry = fresh[fresh.length - 1];
    const reordered = [...fresh];
    reordered.pop();
    reordered.splice(toIdx, 0, newEntry);
    setEntries(reordered);
    await supabase.rpc('reorder_queue', {
      p_user_id: userId,
      p_template_id: templateId,
      p_ordered_ids: reordered.map((e) => e.id),
    });
  };

  const clearDragState = () => {
    setDragSourceIdx(null);
    setDragOverIdx(null);
    setDragOverEnd(false);
  };

  if (!template) return <div className="pools-container">{t.loading}</div>;

  return (
    <div className="pools-container queue-editor dr" data-mtab={mobileTab}>
      <div className="pools-header">
        <button className="pool-lobby-back" onClick={onBack}>← {t.back}</button>
        <h2>{template.name}</h2>
        <div className="queue-editor-count">{entries.length} {t.playersCount}</div>
      </div>

      {error && <div className="pool-form-error">{error}</div>}

      {/* Phone only (hidden by CSS on larger screens) */}
      <div className="draft-mobile-tabs" role="tablist">
        {[
          ['list', `${lang === 'fr' ? 'Ma liste' : 'Your list'} (${entries.length})`],
          ['add', lang === 'fr' ? 'Ajouter des joueurs' : 'Add players'],
        ].map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={mobileTab === key}
            className={mobileTab === key ? 'active' : ''}
            onClick={() => setMobileTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="queue-editor-layout">
        <div className="queue-editor-available">
          <h4>{t.availablePlayers}</h4>
          <div className="draft-filters">
            <input
              type="text"
              className="draft-search"
              placeholder={t.searchPlayer}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="dr-pos-chips" role="group">
              {[['ALL', t.allShort], ['F', 'F'], ['D', 'D'], ['G', 'G'], ['T', t.teamShort]].map(([v, l]) => (
                <button key={v} type="button" className={posFilter === v ? 'active' : ''} onClick={() => setPosFilter(v)}>{l}</button>
              ))}
            </div>
            <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}>
              {teams.map((tm) => (
                <option key={tm} value={tm}>{tm === 'ALL' ? t.allTeams : tm}</option>
              ))}
            </select>
            <label className="draft-filter-toggle">
              <input type="checkbox" checked={playoffOnly} onChange={(e) => setPlayoffOnly(e.target.checked)} />
              {t.playoffOnly}
            </label>
            <label className="draft-filter-toggle">
              <input type="checkbox" checked={hideInjured} onChange={(e) => setHideInjured(e.target.checked)} />
              {t.hideInjured}
            </label>
          </div>
          {playersLoading && <div className="pools-empty">{t.loadingPlayers}</div>}
          {!playersLoading && (
            <div className="draft-players-list">
              {filteredPlayers.slice(0, 200).map((p, idx) => {
                const injStatus = injuryMap?.get(normName(p.name));
                return (
                  <button
                    type="button"
                    key={p.id}
                    className="draft-player-row dr-prow draggable"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'copy';
                      e.dataTransfer.setData('application/x-queue-add', JSON.stringify(p));
                    }}
                    onClick={() => !busy && handleAdd(p)}
                    title={injStatus || t.addPlayerHint}
                  >
                    <span className="dr-prow-rank">{idx + 1}</span>
                    <img className="dr-prow-logo" alt="" loading="lazy" src={nhlLogo(p.team)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                    <span className="dr-prow-main">
                      <span className="dr-prow-name">{p.name}</span>
                      <span className="dr-prow-meta">
                        <b className="dr-slot-pos">{p.position === 'T' ? t.teamWord : p.position}</b>
                        <span className="dr-prow-team">{p.position === 'T' ? t.standingsPts : p.team}</span>
                        {injStatus && <span className="dr-tag inj" title={injStatus}>{abbrevInjury(injStatus)}</span>}
                        {p.position !== 'T' && <span className="dr-tag cap">{fmtCap(p.capHit ?? NHL_CAP_MIN)}</span>}
                      </span>
                    </span>
                    <span className="dr-prow-pts"><b>{draftPts(p)}</b><small>{p.lastPoints != null ? `${t.pts} ${LAST_SEASON}` : t.pts}</small></span>
                  </button>
                );
              })}
              {filteredPlayers.length > 200 && (
                <div className="draft-list-footer">{t.showingFirst} 200 / {filteredPlayers.length}</div>
              )}
              {filteredPlayers.length === 0 && (
                <div className="pools-empty">{t.noPlayers}</div>
              )}
            </div>
          )}
        </div>

        <div
          className={`queue-editor-ranked ${dragOverEnd ? 'drag-over-zone' : ''}`}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes('application/x-queue-add') ||
                e.dataTransfer.types.includes('application/x-queue-reorder')) {
              e.preventDefault();
              e.dataTransfer.dropEffect = e.dataTransfer.types.includes('application/x-queue-reorder') ? 'move' : 'copy';
            }
          }}
          onDragEnter={(e) => {
            // Only flag end zone if not hovering a child row
            if (e.target === e.currentTarget) setDragOverEnd(true);
          }}
          onDragLeave={(e) => {
            if (e.target === e.currentTarget) setDragOverEnd(false);
          }}
          onDrop={(e) => {
            // Fallback: drop on empty area of panel → append
            if (dragOverIdx !== null) return;   // a child handled it
            e.preventDefault();
            const addJson = e.dataTransfer.getData('application/x-queue-add');
            const reorderJson = e.dataTransfer.getData('application/x-queue-reorder');
            if (addJson) {
              const player = JSON.parse(addJson);
              addPlayerAtIndex(player, entries.length);
            } else if (reorderJson) {
              const { fromIdx } = JSON.parse(reorderJson);
              moveEntryToIndex(fromIdx, entries.length);
            }
            clearDragState();
          }}
        >
          <h4>{t.yourQueue}</h4>
          {entries.length === 0 && <div className="pools-empty">{t.queueEmpty}</div>}
          {entries.map((e, idx) => (
            <div
              key={e.id}
              className={`queue-entry-row qe-row ${dragSourceIdx === idx ? 'dragging' : ''} ${dragOverIdx === idx ? 'drop-above' : ''}`}
              draggable={!busy}
              onDragStart={(ev) => {
                ev.dataTransfer.effectAllowed = 'move';
                ev.dataTransfer.setData('application/x-queue-reorder', JSON.stringify({ fromIdx: idx, entryId: e.id }));
                setDragSourceIdx(idx);
              }}
              onDragEnd={clearDragState}
              onDragOver={(ev) => {
                if (ev.dataTransfer.types.includes('application/x-queue-add') ||
                    ev.dataTransfer.types.includes('application/x-queue-reorder')) {
                  ev.preventDefault();
                  ev.stopPropagation();
                  setDragOverIdx(idx);
                  setDragOverEnd(false);
                }
              }}
              onDragLeave={() => setDragOverIdx((cur) => (cur === idx ? null : cur))}
              onDrop={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                const addJson = ev.dataTransfer.getData('application/x-queue-add');
                const reorderJson = ev.dataTransfer.getData('application/x-queue-reorder');
                if (addJson) {
                  const player = JSON.parse(addJson);
                  addPlayerAtIndex(player, idx);
                } else if (reorderJson) {
                  const { fromIdx } = JSON.parse(reorderJson);
                  moveEntryToIndex(fromIdx, idx);
                }
                clearDragState();
              }}
            >
              <span className="queue-drag-handle" title={t.dragToReorder}>⋮⋮</span>
              <span className="dr-prow-rank">{e.rank}</span>
              <img className="dr-prow-logo" alt="" loading="lazy" src={nhlLogo(e.team)} onError={(ev) => { ev.currentTarget.style.visibility = 'hidden'; }} />
              <span className="dr-prow-main">
                <span className="dr-prow-name">{e.player_name}</span>
                <span className="dr-prow-meta">
                  <b className="dr-slot-pos">{e.position === 'T' ? t.teamWord : e.position}</b>
                  <span className="dr-prow-team">{e.position === 'T' ? t.standingsPts : e.team}</span>
                  {e.position !== 'T' && <span className="dr-tag cap">{fmtCap(capById.get(pkey(e.player_id)) ?? NHL_CAP_MIN)}</span>}
                </span>
              </span>
              <button className="queue-remove-btn" onClick={() => handleRemove(e.id)} disabled={busy} title={t.remove}>×</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const TR = {
  en: {
    back: 'Back to templates',
    loading: 'Loading…',
    loadingPlayers: 'Loading NHL players…',
    playersCount: 'players',
    availablePlayers: 'Add a Player',
    yourQueue: 'Your Queue (ranked)',
    queueEmpty: 'Queue is empty. Click players on the left to add them.',
    searchPlayer: 'Search player…',
    allPos: 'All positions',
    forwards: 'Forwards',
    defense: 'Defense',
    goalies: 'Goalies',
    allTeams: 'All teams',
    pts: 'pts',
    showingFirst: 'Showing first',
    noPlayers: 'No players match filters',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
    playoffOnly: 'Playoffs only',
    hideInjured: 'Hide injured',
    allShort: 'All', teamShort: 'Team', teamWord: 'TEAM', standingsPts: 'standings points',
    addPlayerHint: 'Click to add or drag into queue',
    dragToReorder: 'Drag to reorder',
    errRosters: 'Could not load NHL players',
    errAdd: 'Could not add player',
    errRemove: 'Could not remove player',
  },
  fr: {
    back: 'Retour aux listes',
    loading: 'Chargement…',
    loadingPlayers: 'Chargement des joueurs NHL…',
    playersCount: 'joueurs',
    availablePlayers: 'Ajouter un joueur',
    yourQueue: 'Votre liste (classée)',
    queueEmpty: 'Liste vide. Cliquez les joueurs à gauche pour les ajouter.',
    searchPlayer: 'Chercher un joueur…',
    allPos: 'Toutes positions',
    forwards: 'Attaquants',
    defense: 'Défense',
    goalies: 'Gardiens',
    allTeams: 'Toutes équipes',
    pts: 'pts',
    showingFirst: 'Affichage des premiers',
    noPlayers: 'Aucun joueur ne correspond',
    moveUp: 'Monter',
    moveDown: 'Descendre',
    remove: 'Retirer',
    playoffOnly: 'Séries seulement',
    hideInjured: 'Masquer les blessés',
    allShort: 'Tous', teamShort: 'Équipe', teamWord: 'ÉQUIPE', standingsPts: 'points au classement',
    addPlayerHint: 'Cliquez pour ajouter ou glissez dans la liste',
    dragToReorder: 'Glisser pour réorganiser',
    errRosters: 'Impossible de charger les joueurs',
    errAdd: "Impossible d'ajouter",
    errRemove: 'Impossible de retirer',
  },
};
